import type { Asset } from 'contentful'

import type {
  TypeAppWithoutUnresolvableLinksResponse,
  TypeDailyTipWithoutUnresolvableLinksResponse,
  TypeLibraryCategoryWithoutUnresolvableLinksResponse,
  TypeSessionWithoutUnresolvableLinksResponse,
  TypeSettingsPageWithoutUnresolvableLinksResponse,
  TypeStepWithoutUnresolvableLinksResponse,
  TypeSubjectWithoutUnresolvableLinksResponse,
  TypeViewWithoutUnresolvableLinksResponse,
  TypeWeekWithoutUnresolvableLinksResponse,
} from '../types/generated/contentful'
import type {
  AppContent,
  ContentAsset,
  ContentAssetDetails,
  ContentLibraryCategory,
  ContentSession,
  ContentSettingsPage,
  ContentStep,
  ContentSubjectSummary,
  ContentView,
  DashboardContentSubject,
  DashboardContentTip,
  DashboardContentWeek,
} from './contentful-types'
import {
  appContentSchema,
  contentAssetDetailsSchema,
  contentAssetSchema,
  contentLibraryCategorySchema,
  contentSessionSchema,
  contentSettingsPageSchema,
  contentStepSchema,
  contentSubjectSummarySchema,
  contentViewSchema,
  dashboardContentSubjectSchema,
  dashboardContentTipSchema,
  dashboardContentWeekSchema,
} from './contentful-types'

type ContentfulAsset = Asset<'WITHOUT_UNRESOLVABLE_LINKS', string>

function mapAssetDetails(
  details: ContentAssetDetails | null | undefined,
): ContentAssetDetails | null {
  if (details == null) return null

  return contentAssetDetailsSchema.parse({
    image: details.image,
    video: details.video,
  })
}

function mapContentAsset(
  asset: ContentfulAsset | null | undefined,
): ContentAsset | null {
  if (asset == null) return null

  const file = asset.fields.file

  return contentAssetSchema.parse({
    id: asset.sys.id,
    url: file?.url,
    contentType: file?.contentType ?? '',
    fileName: file?.fileName,
    details: mapAssetDetails(file?.details),
  })
}

function mapContentView(
  view: TypeViewWithoutUnresolvableLinksResponse | null | undefined,
): ContentView | null {
  if (view == null) return null

  return contentViewSchema.parse({
    id: view.sys.id,
    pageName: view.fields.pageName,
    headline: view.fields.headline,
    subheadline: view.fields.subheadline,
    textHeader: view.fields.textHeader,
    textContent: view.fields.textContent,
    primaryButtonText: view.fields.primaryButtonText,
    secondaryButtonText: view.fields.secondaryButtonText,
    additionalTextSections: view.fields.additionalTextSections,
  })
}

function mapContentSettingsPage(
  settingsPage:
    | TypeSettingsPageWithoutUnresolvableLinksResponse
    | null
    | undefined,
): ContentSettingsPage | null {
  if (settingsPage == null) return null

  return contentSettingsPageSchema.parse({
    id: settingsPage.sys.id,
    displayName: settingsPage.fields.displayName,
    header: settingsPage.fields.header,
    termOfServiceText: settingsPage.fields.termOfServiceText,
    termOfServiceLink: settingsPage.fields.termOfServiceLink,
    privacyPolicyText: settingsPage.fields.privacyPolicyText,
    privacyPolicyLink: settingsPage.fields.privacyPolicyLink,
  })
}

export function mapAppContent(
  app: TypeAppWithoutUnresolvableLinksResponse,
): AppContent {
  return appContentSchema.parse({
    id: app.sys.id,
    name: app.fields.name,
    settingsPage: mapContentSettingsPage(app.fields.settingsPage),
    onboardingIntroStep: mapContentView(app.fields.onboardingIntroStep),
    onboardingCreateProfile: mapContentView(app.fields.onboardingCreateProfile),
    onboardingPaymentStep: mapContentView(app.fields.onboardingPaymentStep),
    warmupSubject: mapDashboardContentSubject(app.fields.warmupSubject),
    sessionDefinition: app.fields.sessionDefinition,
  })
}

function mapContentSubjectSummary(
  subject: TypeSubjectWithoutUnresolvableLinksResponse,
): ContentSubjectSummary {
  return contentSubjectSummarySchema.parse({
    id: subject.sys.id,
    title: subject.fields.title,
    icon: mapContentAsset(subject.fields.icon),
  })
}

function mapContentStep(
  step: TypeStepWithoutUnresolvableLinksResponse,
): ContentStep {
  return contentStepSchema.parse({
    id: step.sys.id,
    title: step.fields.title,
    description: step.fields.description,
    media: (step.fields.media ?? [])
      .map((asset) => mapContentAsset(asset))
      .filter((asset): asset is ContentAsset => asset != null),
    lengthSeconds: step.fields.length,
  })
}

export function mapContentSession(
  session: TypeSessionWithoutUnresolvableLinksResponse,
): ContentSession {
  return contentSessionSchema.parse({
    id: session.sys.id,
    title: session.fields.title,
    description: session.fields.description,
    media: mapContentAsset(session.fields.media),
    lengthMinutes: session.fields.length,
    featured: session.fields.featured,
    availableFree: session.fields.availableFree ?? null,
    category:
      session.fields.category == null
        ? null
        : mapContentSubjectSummary(session.fields.category),
    steps: (session.fields.steps ?? [])
      .map((step) => (step == null ? null : mapContentStep(step)))
      .filter((step): step is ContentStep => step != null),
  })
}

export function mapContentLibraryCategory(
  category: TypeLibraryCategoryWithoutUnresolvableLinksResponse,
): ContentLibraryCategory {
  return contentLibraryCategorySchema.parse({
    id: category.sys.id,
    title: category.fields.title,
    sessions: (category.fields.sessions ?? [])
      .map((session) => (session == null ? null : mapContentSession(session)))
      .filter((session): session is ContentSession => session != null),
  })
}

export function mapDashboardContentWeek(
  week: TypeWeekWithoutUnresolvableLinksResponse,
): DashboardContentWeek {
  return dashboardContentWeekSchema.parse({
    id: week.sys.id,
    week: week.fields.week,
    dailyActivities: week.fields.dailyActivities ?? null,
    sessions: (week.fields.sessions ?? [])
      .map((session) => (session == null ? null : mapContentSession(session)))
      .filter((session): session is ContentSession => session != null),
  })
}

export function mapDashboardContentSubject(
  subject: TypeSubjectWithoutUnresolvableLinksResponse | null | undefined,
): DashboardContentSubject | null {
  if (subject == null) return null

  return dashboardContentSubjectSchema.parse({
    id: subject.sys.id,
    title: subject.fields.title,
    programId: subject.fields.program?.sys.id,
    icon: mapContentAsset(subject.fields.icon),
    weeks: (subject.fields.weeks ?? [])
      .map((week) => (week == null ? null : mapDashboardContentWeek(week)))
      .filter((week): week is DashboardContentWeek => week != null),
  })
}

export function mapDashboardContentTip(
  tip: TypeDailyTipWithoutUnresolvableLinksResponse,
): DashboardContentTip {
  return dashboardContentTipSchema.parse({
    id: tip.sys.id,
    day: tip.fields.day,
    tip: tip.fields.tip,
    picture: mapContentAsset(tip.fields.picture),
  })
}
